/** calculate the checksum of a file in the browser.
 *
 * This is ripped from ActiveStorage's implementation, with the addition of `checkPromise` to expose a promise-ified version.
 */
import SparkMD5 from 'spark-md5';

declare interface FilePrototype {
  slice: (start?: number, end?: number, contentType?: string) => Blob;
  mozSlice: (start?: number, end?: number, contentType?: string) => Blob;
  webkitSlice: (start?: number, end?: number, contentType?: string) => Blob;
}

const fileProto: FilePrototype = File.prototype as unknown as FilePrototype;

const fileSlice = fileProto.slice || fileProto.mozSlice || fileProto.webkitSlice;

export class FileChecksum {
  private fileReader?: FileReader;
  private callback?: (error: String | null, checksum: String | null) => void;
  chunkSize: number;
  chunkCount: number;
  chunkIndex: number;
  file: File;
  md5Buffer: any;

  static async checkPromise(file: File) {
    const promise = new Promise((response, reject) => {
      const instance = new FileChecksum(file);
      instance.create((isError, checksum) => {
        if (isError) {
          reject(isError);
        } else {
          response(checksum);
        }
      });
    });
    return promise;
  }

  constructor(file: File) {
    this.file = file;
    this.chunkSize = 2097152; // 2MB
    this.chunkCount = Math.ceil(this.file.size / this.chunkSize);
    this.chunkIndex = 0;
  }

  create(callback: (error: String | null, checksum: String | null) => void) {
    this.callback = callback;
    this.md5Buffer = new SparkMD5.ArrayBuffer();
    this.fileReader = new FileReader();
    this.fileReader.addEventListener('load', event => this.fileReaderDidLoad(event));
    this.fileReader.addEventListener('error', event => this.fileReaderDidError(event));
    this.readNextChunk();
  }

  fileReaderDidLoad(event: ProgressEvent<FileReader>) {
    event!;
    if (!event.target) {
      this.callback!(`Error reading event`, '');
      return false;
    }

    this.md5Buffer.append(event.target.result);

    if (!this.readNextChunk()) {
      const binaryDigest = this.md5Buffer.end(true);
      const base64digest = btoa(binaryDigest);
      this.callback!(null, base64digest);
    }
  }

  fileReaderDidError(event: ProgressEvent<FileReader>) {
    this.callback!(`Error reading ${this.file.name}`, null);
  }

  readNextChunk() {
    if (this.chunkIndex < this.chunkCount || (this.chunkIndex == 0 && this.chunkCount == 0)) {
      const start = this.chunkIndex * this.chunkSize;
      const end = Math.min(start + this.chunkSize, this.file.size);
      const bytes = fileSlice.call(this.file, start, end);
      this.fileReader!.readAsArrayBuffer(bytes);
      this.chunkIndex++;
      return true;
    } else {
      return false;
    }
  }
}
